import {
  Box,
  ButtonIcon,
  IconOverflow,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
  TextLinkButton,
  TooltipRenderer,
} from 'braid-design-system';

import { Placeholder } from './Placeholder';

import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';

const SeekIcon = () => (
  <TooltipRenderer tooltip={<Text>View SEEK Profile</Text>}>
    {({ triggerProps }) => (
      <Box
        aria-label="View SEEK Profile"
        style={{ cursor: 'pointer' }}
        {...triggerProps}
      >
        <Box
          component="img"
          src="https://integration.seek.com/assets/seek-profile/view-profile-icon/seek-profile.svg"
        />
      </Box>
    )}
  </TooltipRenderer>
);

/**
 * Example of displaying a 'View SEEK Profile' link in a list of candidates
 */
export const ViewSeekProfileLinkExample = () => (
  <ThemedExample>
    <Stack space="large">
      <Text size="large" weight="strong">
        Barista (Part-time)
      </Text>
      <TabsProvider onChange={() => undefined} selectedItem="applications">
        <Stack space="medium">
          <Tabs label="Applications">
            <Tab item="applications">Applications</Tab>
            {/* Nonexistent second tab for demonstration purposes */}
            <Tab item="applications">Job details</Tab>
          </Tabs>
          <TabPanels>
            <TabPanel item="applications">
              <Table label="Applications">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      <Text>Name</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Source</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Profile</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Status</Text>
                    </TableHeaderCell>
                    <TableHeaderCell width="content" align="right">
                      <Text>Actions</Text>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    {
                      name: 'John Smith',
                      source: 'SEEK',
                      seekProfile: true,
                      submitted: 'Today',
                      status: 'New',
                    },
                    {
                      name: 'Sarah Johnson',
                      source: 'Careers website',
                      seekProfile: true,
                      submitted: 'Today',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Michael Brown',
                      source: 'Other',
                      seekProfile: false,
                      submitted: 'Today',
                      status: 'Offered',
                    },
                    {
                      name: 'Emma Wilson',
                      source: 'SEEK',
                      seekProfile: true,
                      submitted: 'Today',
                      status: 'New',
                    },
                    {
                      name: 'David Lee',
                      source: 'Other',
                      seekProfile: true,
                      submitted: 'Today',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Rachel Green',
                      source: 'SEEK',
                      seekProfile: true,
                      submitted: 'Yesterday',
                      status: 'New',
                    },
                    {
                      name: 'James Taylor',
                      source: 'Careers website',
                      seekProfile: true,
                      submitted: 'Yesterday',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Lisa Anderson',
                      source: 'Other',
                      seekProfile: false,
                      submitted: 'Yesterday',
                      status: 'Offered',
                    },
                    {
                      name: 'Peter Wright',
                      source: 'SEEK',
                      seekProfile: true,
                      submitted: '2 days ago',
                      status: 'New',
                    },
                    {
                      name: 'Amy Chen',
                      source: 'Careers website',
                      seekProfile: false,
                      submitted: '2 days ago',
                      status: 'Shortlisted',
                    },
                  ].map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>
                        <Text>{row.name}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{row.source}</Text>
                      </TableCell>
                      <TableCell>
                        {row.seekProfile ? (
                          <Text>
                            <TextLinkButton onClick={() => undefined}>
                              View SEEK Profile
                            </TextLinkButton>
                          </Text>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Text>{row.status} </Text>
                      </TableCell>
                      <TableCell width="content" align="right">
                        <ButtonIcon
                          icon={<IconOverflow />}
                          label="Actions"
                          variant="transparent"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Stack>
      </TabsProvider>
    </Stack>
  </ThemedExample>
);

export const ViewSeekProfileIconButtonExample = () => (
  <ThemedExample>
    <Stack space="large">
      <Text size="large" weight="strong">
        Barista (Part-time)
      </Text>
      <TabsProvider onChange={() => undefined} selectedItem="applications">
        <Stack space="medium">
          <Tabs label="Applications">
            <Tab item="applications">Applications</Tab>
            <Tab item="applications">Job details</Tab>
          </Tabs>
          <TabPanels>
            <TabPanel>
              <Table label="Applications">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      <Text>Name</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Source</Text>
                    </TableHeaderCell>
                    <TableHeaderCell align="center">
                      <Text>Profile</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text>Status</Text>
                    </TableHeaderCell>
                    <TableHeaderCell width="content" align="right">
                      <Text>Actions</Text>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    {
                      name: 'John Smith',
                      source: 'SEEK',
                      icon: <SeekIcon />,
                      submitted: 'Today',
                      status: 'New',
                    },
                    {
                      name: 'Sarah Johnson',
                      source: 'Careers website',
                      icon: <SeekIcon />,
                      submitted: 'Today',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Michael Brown',
                      source: 'Other',
                      usePlaceholder: true,

                      submitted: 'Today',
                      status: 'Offered',
                    },
                    {
                      name: 'Emma Wilson',
                      source: 'SEEK',
                      icon: <SeekIcon />,
                      submitted: 'Today',
                      status: 'New',
                    },
                    {
                      name: 'David Lee',
                      source: 'Other',
                      icon: <SeekIcon />,
                      submitted: 'Today',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Rachel Green',
                      source: 'SEEK',
                      icon: <SeekIcon />,
                      submitted: 'Yesterday',
                      status: 'New',
                    },
                    {
                      name: 'James Taylor',
                      source: 'Careers website',
                      icon: <SeekIcon />,
                      submitted: 'Yesterday',
                      status: 'Shortlisted',
                    },
                    {
                      name: 'Lisa Anderson',
                      source: 'Other',
                      usePlaceholder: true,
                      submitted: 'Yesterday',
                      status: 'Offered',
                    },
                    {
                      name: 'Peter Wright',
                      source: 'SEEK',
                      icon: <SeekIcon />,
                      submitted: '2 days ago',
                      status: 'New',
                    },
                    {
                      name: 'Amy Chen',
                      source: 'Careers website',
                      usePlaceholder: true,
                      submitted: '2 days ago',
                      status: 'Shortlisted',
                    },
                  ].map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>
                        <Text>{row.name}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{row.source}</Text>
                      </TableCell>
                      <TableCell align="center">
                        <Box style={{ width: 24, height: 24 }}>
                          {row.usePlaceholder ? (
                            <Placeholder width="24px" height="24px" />
                          ) : (
                            row.icon
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Text>{row.status}</Text>
                      </TableCell>
                      <TableCell width="content" align="right">
                        <ButtonIcon
                          icon={<IconOverflow />}
                          label="Actions"
                          variant="transparent"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Stack>
      </TabsProvider>
    </Stack>
  </ThemedExample>
);

export const ViewSeekProfileTabExample = () => (
  <ThemedExample>
    <Stack space="large">
      <Text size="large" weight="strong">
        Rachel Green
      </Text>
      <TabsProvider>
        <Stack space="medium">
          <Tabs label="Candidate">
            <Tab item="overview">Overview</Tab>
            <Tab item="applications">Job applications</Tab>
            <Tab item="seekProfile">SEEK Profile</Tab>
          </Tabs>
          <TabPanels>
            <TabPanel>
              <Placeholder width="full" height="128px" />
            </TabPanel>
            <TabPanel>
              <Placeholder width="full" height="128px" />
            </TabPanel>
            <TabPanel>
              <Placeholder width="full" height="128px" />
            </TabPanel>
          </TabPanels>
        </Stack>
      </TabsProvider>
    </Stack>
  </ThemedExample>
);
